import {
  CONTRAST_THRESHOLDS,
  contrastRatio,
  cssVar,
  hexToOklch,
  oklchToHex,
  parseSourceColorChannels,
  resolveCalendarColours,
  THEME_CALENDAR_COLOURS_KEY,
} from '@mfw/common';

import type { CalendarColourMode, Oklch } from '@mfw/common';

import {
  measureOnSurfaceMarks,
  readMarkSurfaces,
  worstContrast,
  type ThemeValueReader,
} from './onSurface';

/**
 * `--mfw-palette-schedule-<tone>` — the colour a calendar tone is drawn in
 * under the calendar colour mode in force (spec §6), and the accessor for
 * naming it from application code.
 *
 * Runtime aliases outside `THEME_VARS`, for the same reasons as
 * `onSurfaceMark` (see `theme/onSurface.ts`): each one is PICKED by
 * measurement — and here also by a stored setting — so there is no single
 * source var for the catalog to derive it from. The root writer
 * (`writeResolvedTheme`) measures all eight on every apply;
 * `assets/scss/main.scss` declares the pre-JS default; Tailwind exposes them as
 * `schedule.<tone>`. `scheduleMarks.spec.ts` binds the accessor to all three.
 *
 * They are MARKS — edge stripes, day dots, rings, icons — measured at the 3:1
 * a non-text mark needs. Text drawn beside them takes a measured text ink
 * (`onSurface.*`), never one of these.
 */
export const SCHEDULE_TONES = [
  'workout',
  'booking',
  'nutrition',
  'note',
  'questionnaire',
  'questionnaireOverdue',
  'missed',
  'cancelled',
] as const;

export type ScheduleTone = (typeof SCHEDULE_TONES)[number];

/** `scheduleVar('workout')` -> `var(--mfw-palette-schedule-workout)`. */
export const scheduleVar = (tone: ScheduleTone) =>
  `var(--mfw-palette-schedule-${tone})`;

/**
 * The calendar colour mode stamped on `root` (`data-mfw-calendar`), validated
 * the way a stored record is: anything that is not a known mode — absent,
 * unknown, mis-cased — is Brand, the specified default.
 */
export const calendarModeOf = (root: HTMLElement) =>
  resolveCalendarColours({
    [THEME_CALENDAR_COLOURS_KEY]: root.dataset.mfwCalendar,
  });

/**
 * Stamps a calendar colour mode on `root` — validated, so the attribute never
 * carries a value no stylesheet or reader matches. The schedule vars are
 * measured FROM this attribute, so it goes on before them.
 */
export const stampCalendarMode = (
  root: HTMLElement,
  mode: CalendarColourMode,
) => {
  root.dataset.mfwCalendar = resolveCalendarColours({
    [THEME_CALENDAR_COLOURS_KEY]: mode,
  });
};

/** How far the nutrition shade sits from the primary mark, in OKLCH L. */
export const NUTRITION_SHADE_DELTA_L = 0.15;

/**
 * Circular distance between two hue angles, in degrees (0..180): 359° and 1°
 * are 2° apart, not 358°.
 */
export const hueDistance = (a: number, b: number) => {
  const distance = Math.abs(a - b) % 360;
  return Math.min(distance, 360 - distance);
};

/** A Brand tone this close in hue to a status mark reads as that status. */
export const STATUS_CLASH_HUE_DEGREES = 35;

/**
 * Below this OKLCH chroma a colour is near-grey: its hue angle is noise, and
 * nobody reads it as red or amber.
 */
export const STATUS_CLASH_MIN_CHROMA = 0.06;

/**
 * Whether a Brand tone would be mistaken for a status mark: hues under 35°
 * apart, and both colourful enough (chroma ≥ 0.06) for hue to mean anything.
 * A red brand's workouts look "missed", an amber one's look "overdue".
 */
export const huesClash = (tone: Oklch, status: Oklch) =>
  tone.C >= STATUS_CLASH_MIN_CHROMA &&
  status.C >= STATUS_CLASH_MIN_CHROMA &&
  hueDistance(tone.H, status.H) < STATUS_CLASH_HUE_DEGREES;

/**
 * Any colour a theme layer can hold, as `#rrggbb` — through the engine's own
 * parser, so a tenant's verbatim `rgb()` measures too. Alpha is ignored: every
 * candidate measured here is an opaque ramp step or body ink. `undefined` for a
 * value no parser reads.
 */
const toHex = (value: string) => {
  try {
    return `#${parseSourceColorChannels(value)
      .map((channel) =>
        Math.max(0, Math.min(255, Math.round(channel)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')}`;
  } catch {
    return undefined;
  }
};

/** The same colour in OKLCH, for its hue and chroma. */
const toOklch = (value: string) => {
  const hex = toHex(value);
  return hex ? hexToOklch(hex) : undefined;
};

const clashesWithAny = (tone: string, statuses: readonly string[]) => {
  const toneColor = toOklch(tone);
  if (!toneColor) return false;
  return statuses.some((status) => {
    const statusColor = toOklch(status);
    return statusColor ? huesClash(toneColor, statusColor) : false;
  });
};

/**
 * Brand-mode nutrition: the primary mark shifted by ΔL 0.15 in OKLCH, so a
 * meal and a workout on the same day read as two things in one brand.
 *
 * First towards higher contrast with the page (`page`); if the shifted colour
 * is under 3:1 on any of `surfaces`, the opposite direction from the original;
 * if that fails too, the primary mark itself — the cards still differ by icon
 * and meal chip. What gets measured is the final hex, after gamut mapping and
 * rounding, because that is what gets painted. A direction that lands back on
 * the mark's own colour (white shifted lighter stays white — the body-ink
 * fallback on a tile with no readable brand step) is no shade, so it fails too.
 *
 * Ramp steps were rejected for this (spec §6.3): they sit only ~0.07 apart on
 * the dark tiles, and on a light tile the default green has no second
 * readable step left at all.
 */
export function nutritionShade(
  primaryMark: string,
  page: string,
  surfaces: readonly string[],
) {
  const markHex = toHex(primaryMark);
  if (!markHex) return primaryMark;
  const base = hexToOklch(markHex);

  const shifted = [1, -1].map((sign) =>
    oklchToHex({ ...base, L: base.L + sign * NUTRITION_SHADE_DELTA_L }),
  );
  const contrastWithPage = (color: string) => {
    try {
      return contrastRatio(color, page);
    } catch {
      return 0;
    }
  };
  const [away, towards] =
    contrastWithPage(shifted[1]) > contrastWithPage(shifted[0])
      ? [shifted[1], shifted[0]]
      : shifted;

  return (
    [away, towards].find(
      (color) =>
        color !== markHex.toLowerCase() &&
        worstContrast(color, surfaces) >= CONTRAST_THRESHOLDS.ui,
    ) ?? primaryMark
  );
}

/** The measured colours the eight tones are built from, per mode. */
export type ScheduleMarks = {
  primary: string;
  accent: string;
  /** The Brand nutrition shade (`nutritionShade`). */
  nutrition: string;
  info: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  /** Body-text grey, `text-secondary` — at least 3:1 on every ladder. */
  grey: string;
};

/**
 * The colour of every tone in a mode (spec §6.2):
 *
 * | tone                 | Brand          | Neutral | Classic   |
 * |----------------------|----------------|---------|-----------|
 * | workout              | primary mark   | grey    | info      |
 * | booking              | accent mark    | grey    | secondary |
 * | nutrition            | primary shade  | grey    | success   |
 * | note                 | grey           | grey    | info      |
 * | questionnaire        | grey           | grey    | warning   |
 * | questionnaireOverdue | warning        | warning | warning   |
 * | missed               | error          | error   | error     |
 * | cancelled            | grey           | grey    | grey      |
 *
 * In Brand mode each brand tone is checked against the error and warning
 * marks on its OWN measured colour: a red primary greys workouts and
 * nutrition, and leaves a blue booking blue.
 */
export function scheduleTones(
  mode: CalendarColourMode,
  marks: ScheduleMarks,
): Record<ScheduleTone, string> {
  const { grey } = marks;
  const statuses = {
    cancelled: grey,
    missed: marks.error,
    questionnaireOverdue: marks.warning,
  };

  if (mode === 'classic') {
    return {
      ...statuses,
      booking: marks.secondary,
      note: marks.info,
      nutrition: marks.success,
      questionnaire: marks.warning,
      workout: marks.info,
    };
  }
  if (mode === 'neutral') {
    return {
      ...statuses,
      booking: grey,
      note: grey,
      nutrition: grey,
      questionnaire: grey,
      workout: grey,
    };
  }

  const brandTone = (color: string) =>
    clashesWithAny(color, [marks.error, marks.warning]) ? grey : color;
  return {
    ...statuses,
    booking: brandTone(marks.accent),
    note: grey,
    nutrition: brandTone(marks.nutrition),
    questionnaire: grey,
    workout: brandTone(marks.primary),
  };
}

/**
 * The colour of all eight tones in `mode`, measured from the theme values in
 * force — without writing anything. `applyScheduleMarks` writes exactly this;
 * the rebrand editor previews a theme that is not on screen with it. Undefined
 * when the theme has no surface to measure against.
 *
 * Built on the same mark picks as `onSurfaceMark` (`measureOnSurfaceMarks`),
 * so the calendar and every other mark agree about one theme.
 */
export function measureScheduleTones(
  mode: CalendarColourMode,
  readThemeValue: ThemeValueReader,
) {
  const surfaces = readMarkSurfaces(readThemeValue);
  if (surfaces.length === 0) return undefined;

  const measured = measureOnSurfaceMarks(readThemeValue);
  const grey =
    readThemeValue('--mfw-palette-text-secondary') ?? cssVar.text.secondary;
  // A family with no readable step at all has no mark; grey still marks.
  const mark = (family: keyof typeof measured) => measured[family] ?? grey;

  const primary = mark('primary');
  return scheduleTones(mode, {
    accent: mark('accent'),
    error: mark('error'),
    grey,
    info: mark('info'),
    nutrition: measured.primary
      ? nutritionShade(measured.primary, surfaces[0], surfaces)
      : grey,
    primary,
    secondary: mark('secondary'),
    success: mark('success'),
    warning: mark('warning'),
  });
}

/**
 * Measures and writes all eight `--mfw-palette-schedule-<tone>` vars for the
 * mode stamped on `root` (`calendarModeOf`). Writes nothing when the theme has
 * no surface to measure against — exactly when the marks are not written either.
 */
export function applyScheduleMarks(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
) {
  const tones = measureScheduleTones(calendarModeOf(root), readThemeValue);
  if (!tones) return;

  for (const tone of SCHEDULE_TONES) {
    root.style.setProperty(`--mfw-palette-schedule-${tone}`, tones[tone]);
  }
}
